<template>
    <div class="domain-section-modal">
        <el-dialog
            :title="$trans('web_skill_ranking.edit_area_of_expertise')"
            :model-value="WebSkillRankingStore.isEditAoEModalOpen"
            width="800px"
            :show-close="false"
            class="custom-modal"
            @close="close"
        >
            <span class="dialog-cancel-text" @click="close">
                {{ $trans("web_skill_ranking.cancel") }}
                <el-icon><Close /></el-icon>
            </span>

            <el-button
                type="primary"
                :loading="WebSkillRankingStore.loading"
                @click="save"
            >
                {{ $trans("web_skill_ranking.save") }}
            </el-button>

            <div class="table-container" v-loading="isLoading">
                <el-input
                    v-model="searchQuery"
                    :placeholder="$trans('web_skill_ranking.search_placeholder')"
                    clearable
                    class="custom-search-input"
                >
                    <template #suffix>
                        <img src="/media/images/search_gray.png" alt="Search" style="width: 16px; height: 16px; opacity: 0.6" />
                    </template>
                </el-input>

                <EasyDataTable
                    v-if="!isLoading"
                    :headers="headers"
                    :items="sortedItems"
                    :search-value="searchQuery"
                    table-class-name="competences-table"
                    :rows-per-page="10"
                    buttons-pagination
                >
                    <template #item-action="{ id, is_selected }">
                        <el-checkbox
                            :model-value="is_selected"
                            @change="handleToggle(id)"
                        />
                    </template>

                    <template #header-name>
                        <div class="custom-table-header">
                            <span @click.stop="changeSort('name')" :class="{ active: sortBy === 'name' }">
                                {{ $trans("web_skill_ranking.expertise_name") }}
                                <SortIcons :columnKey="'name'" :sortBy="sortBy" :sortDesc="sortDesc" />
                            </span>
                        </div>
                    </template>
                </EasyDataTable>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import { mapStores } from "pinia";
import { useWebSkillRankingStore } from "@/stores/WebSkillRankingStore";
import EasyDataTable from "vue3-easy-data-table";
import SortIcons from "../../ProfileDossier/Components/Tables/SortIcons.vue"
import { Close } from "@element-plus/icons-vue";
import axios from "axios";

export default {
    name: "EditRankingAoEModal",
    components: { EasyDataTable, SortIcons, Close },
    data() {
        return {
            localItems: [],
            searchQuery: "",
            sortBy: "name",
            sortDesc: false,
            isLoading: false,
        };
    },
    computed: {
        ...mapStores(useWebSkillRankingStore),
        isModalOpen() {
            return this.WebSkillRankingStore.isEditAoEModalOpen;
        },
        headers() {
            return [
                { text: this.$trans("web_skill_ranking.action"), value: "action", width: 50 },
                { text: this.$trans("web_skill_ranking.expertise_name"), value: "name" },
            ];
        },
        sortedItems() {
            if (!this.sortBy) return this.localItems;
            return [...this.localItems].sort((a, b) => {
                let aVal = a[this.sortBy]?.toLowerCase() || "";
                let bVal = b[this.sortBy]?.toLowerCase() || "";
                if (aVal < bVal) return this.sortDesc ? 1 : -1;
                if (aVal > bVal) return this.sortDesc ? -1 : 1;
                return 0;
            });
        },
    },
    watch: {
        isModalOpen(newValue) {
            if (newValue) {
                this.localItems = [];
                this.fetchData();
                this.sortBy = "name";
                this.sortDesc = false;
            }
        },
    },
    methods: {
        close() {
            this.WebSkillRankingStore.isEditAoEModalOpen = false;
        },
        async fetchData() {
            if (this.isLoading) return;
            
            const currentDomains = this.WebSkillRankingStore.ranking.domains || {};
            const verticalId = currentDomains.vertical;
            
            if (!verticalId) {
                this.$message.warning(this.$trans('web_skill_ranking.select_vertical_first'));
                this.close();
                return;
            }

            this.isLoading = true;
            try {
                const rankingId = this.WebSkillRankingStore.ranking.id;
                const response = await axios.get(`/admin/web-skill-rankings/${rankingId}/domains/aoe?vertical_id=${verticalId}`);
                
                const currentSelectedId = currentDomains.aoe;

                this.localItems = response.data.map((item) => ({
                    ...item,
                    is_selected: item.id === currentSelectedId,
                }));
            } catch (error) {
                console.error(error);
            } finally {
                this.isLoading = false;
            }
        },
        handleToggle(id) {
            this.localItems = this.localItems.map((item) => ({
                ...item,
                is_selected: item.id === id,
            }));
        },
        async save() {
            const selectedItem = this.localItems.find((i) => i.is_selected);
            const selectedId = selectedItem ? selectedItem.id : null;

            if (!this.WebSkillRankingStore.ranking.domains) {
                this.WebSkillRankingStore.ranking.domains = {};
            }
            this.WebSkillRankingStore.ranking.domains.aoe = selectedId;

            try {
                await this.WebSkillRankingStore.saveDomains();
                this.$message.success(this.$trans("web_skill_ranking.data_saved"));
                this.close();
            } catch (e) {
                this.$message.error(this.$trans("web_skill_ranking.something_went_wrong"));
            }
        },
        changeSort(key) {
            if (this.sortBy === key) {
                this.sortDesc = !this.sortDesc;
            } else {
                this.sortBy = key;
                this.sortDesc = false;
            }
        },
    },
};
</script>

<style scoped>
.domain-section-modal :deep(.el-dialog) {
    --el-dialog-width: 750px;
    top: -100px !important;
}
.table-container {
    width: 100%;
    box-sizing: border-box;
    padding: 10px 14px 24px 14px;
    background-color: #ededed;
    font-family: "Barlow", sans-serif;
}
:deep(.el-input) {
    width: 40%;
}
:deep(.el-dialog__header) {
    padding-bottom: 70px;
}
:deep(.el-input__wrapper.is-focus) {
    border: 2px solid #41abc4 !important;
    box-shadow: none !important;
}
.competences-table {
    width: 100%;
    --easy-table-header-font-size: 14px;
    --easy-table-body-row-font-size: 14px;
    --easy-table-body-row-hover-background-color: #fff;
}
:deep(.vue3-easy-data-table) {
    margin-bottom: unset !important;
    border: none !important;
    background-color: #ededed;
}
:deep(.vue3-easy-data-table tr th) {
    background: rgb(237, 237, 237);
    padding: 0 0.5rem;
    color: #3c3c3c;
    font-size: 16px !important;
    height: 24px;
    line-height: 24px;
    border: 2px solid #ededed;
}
:deep(.vue3-easy-data-table tr td) {
    background: #ffffff !important;
    padding: 0.5rem !important;
    border: 2px solid #ededed !important;
    font-size: 16px !important;
    font-weight: 700 !important;
}
:deep(.vue3-easy-data-table__footer) {
    background-color: transparent !important;
    justify-content: center !important;
}
:deep(.pagination__rows-per-page),
:deep(.pagination__items-index) {
    display: none !important;
}
:deep(.buttons-pagination) {
    transform: scale(1.5);
    margin-top: 25px;
}
:deep(.buttons-pagination .item.button) {
    border-radius: 0;
}
:deep(.buttons-pagination .item.button.active) {
    color: #fff;
    background: linear-gradient(
        90deg,
        rgb(65, 171, 196) 0%,
        rgb(119, 214, 241) 100%
    );
    border-color: rgb(65, 171, 196) !important;
}
.custom-table-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    cursor: pointer;
    user-select: none;
}
.custom-table-header span {
    display: flex;
    align-items: center;
    width: 100%;
    justify-content: space-between;
}
:deep(.sortType-icon) {
    display: inline-flex !important;
}
:deep(.vue3-easy-data-table__body tr td:first-child) {
    background-color: #d2e1e5 !important;
    text-align: center !important;
}
:deep(.el-input__wrapper) {
    border-radius: 0 !important;
    margin-bottom: 20px;
    height: 37px;
}
.el-button {
    float: right;
    margin-top: -35px;
    background-color: #41abc4;
    border-color: #41abc4;
    border-radius: unset;
    width: 200px;
    font-size: 16px !important;
    font-weight: 700 !important;
}
.dialog-cancel-text {
    position: absolute;
    top: 5px;
    right: 21px;
    cursor: pointer;
    font-size: 14px;
    color: #909399;
    z-index: 2000;
}
.dialog-cancel-text:hover {
    color: #41abc4;
}
.dialog-cancel-text :deep(.el-icon) {
    top: 3px;
    transform: scale(1.4);
}

:deep(.el-button.is-loading .el-icon.is-loading) {
  z-index: 1;
  background: #7ac4d6;
}

:deep(.next-page__click-button .arrow) {
    margin-top: 21px;
    margin-left: 40px;
}

:deep(.previous-page__click-button .arrow) {
    margin-right: 40px;
    margin-top: 21px;
}

:deep(.el-dialog__title) {
    position: relative;
    font-size: 1.5rem;
    top: 30px;
}
</style>